const SECRET_PATTERN = /\$\{secret:([a-zA-Z0-9_-]+)\}/g;
export const SECRET_TOKEN_PATTERN =
  /\$(\{(s(e(c(r(e(t(:([a-zA-Z0-9_-]*)?)?)?)?)?)?)?)?)?$/;
const SECRET_PREFIX = "${secret:";

// Helper to check if a character is valid in a secret name
const isValidSecretChar = (ch: string): boolean =>
  (ch >= "a" && ch <= "z") ||
  (ch >= "A" && ch <= "Z") ||
  (ch >= "0" && ch <= "9") ||
  ch === "_" ||
  ch === "-";

export const extractSecretNames = (value: string): string[] => {
  const matches = [...value.matchAll(SECRET_PATTERN)];
  return [...new Set(matches.map((match) => match[1]))];
};

export const getAllSecretReferencesFromHeaders = (
  headers: Record<string, string>
): Set<string> =>
  new Set(Object.values(headers).flatMap((value) => extractSecretNames(value)));

const createSecretReference = (secretName: string): string =>
  `\${secret:${secretName}}`;

// Find the range of an incomplete secret at or before cursor position
const findIncompleteSecretRange = (
  text: string,
  cursor: number
): { start: number; end: number } | null => {
  if (cursor <= 0) {
    return null;
  }

  // Look backward for the start of a potential secret token
  const startIdx = text.lastIndexOf("$", cursor - 1);
  if (startIdx === -1) {
    return null;
  }

  // Check if this looks like a secret pattern
  const afterDollar = text.slice(startIdx, cursor);
  const isPartialSecret =
    afterDollar === "$" ||
    SECRET_PREFIX.startsWith(afterDollar) ||
    afterDollar.startsWith(SECRET_PREFIX);

  if (!isPartialSecret) {
    return null;
  }

  // Find the end of the incomplete secret
  let end = startIdx + SECRET_PREFIX.length;
  while (end < text.length) {
    const ch = text[end];
    if (ch === "}") {
      return null; // It's complete, not incomplete
    }
    if (!isValidSecretChar(ch)) {
      break; // Found the end
    }
    end += 1;
  }

  return { start: startIdx, end };
};

export const collectReferencedSecrets = (
  headers: Record<string, string>,
  drafts: Array<{ value: string }>
): Set<string> => {
  const allValues = [
    ...Object.values(headers),
    ...drafts.map((d) => d.value).filter(Boolean),
  ];
  return new Set(allValues.flatMap((value) => extractSecretNames(value)));
};

export const buildSecretInsertion = (
  value: string,
  cursorPosition: number,
  secretName: string
): { newValue: string; newCursorPosition: number } => {
  const secretReference = createSecretReference(secretName);
  const range = findIncompleteSecretRange(value, cursorPosition);

  if (range) {
    // Replace incomplete secret
    return {
      newValue:
        value.slice(0, range.start) + secretReference + value.slice(range.end),
      newCursorPosition: range.start + secretReference.length,
    };
  }

  // Insert at cursor
  return {
    newValue:
      value.slice(0, cursorPosition) +
      secretReference +
      value.slice(cursorPosition),
    newCursorPosition: cursorPosition + secretReference.length,
  };
};
